import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Bell, BellRing, Settings, Send, Trash2, RefreshCw, 
  AlertTriangle, CheckCircle, Clock, CreditCard,
  Calendar, Ticket, Server, Shield, Eye,
  MessageCircle, Zap, Save
} from 'lucide-react'
import { adminApi } from '../services/ApiService'
import './NotificationsHub.css'

const CHANNEL_ICONS = { telegram: MessageCircle, webhook: Zap, internal: Bell }
const CHANNEL_LABELS = { telegram: 'Telegram', webhook: 'Webhook', internal: 'Panel' }

const EVENT_CATEGORIES = {
  device: { label: 'Dispositivos', icon: Server, color: '#3b82f6', events: [
    { key: 'device.offline', label: 'Dispositivo desconectado' },
    { key: 'device.online', label: 'Dispositivo reconectado' },
    { key: 'device.temp_high', label: 'Temperatura alta (>35°C)' },
    { key: 'device.temp_low', label: 'Temperatura baja (<15°C)' },
    { key: 'device.humidity_high', label: 'Humedad alta (>80%)' },
    { key: 'device.humidity_low', label: 'Humedad baja (<30%)' },
    { key: 'device.ota_complete', label: 'OTA completado' },
    { key: 'device.ota_failed', label: 'OTA fallido' },
  ]},
  reseller: { label: 'Revendedores', icon: Shield, color: '#f59e0b', events: [
    { key: 'reseller.pending', label: 'Nueva solicitud de revendedor' },
    { key: 'reseller.activated', label: 'Revendedor activado' },
    { key: 'reseller.sale', label: 'Venta de revendedor' },
    { key: 'reseller.payout_due', label: 'Liquidación pendiente' },
  ]},
  subscription: { label: 'Suscripciones', icon: CreditCard, color: '#10b981', events: [
    { key: 'sub.new', label: 'Nueva suscripción' },
    { key: 'sub.cancelled', label: 'Suscripción cancelada' },
    { key: 'sub.trial_expiring', label: 'Trial por vencer (3 días)' },
    { key: 'sub.trial_expired', label: 'Trial expirado' },
    { key: 'sub.payment_received', label: 'Pago recibido' },
    { key: 'sub.payment_failed', label: 'Pago fallido' },
  ]},
  support: { label: 'Soporte', icon: Ticket, color: '#f59e0b', events: [
    { key: 'ticket.new', label: 'Nuevo ticket' },
    { key: 'ticket.comment', label: 'Comentario en ticket' },
    { key: 'ticket.resolved', label: 'Ticket resuelto' },
    { key: 'ticket.escalated', label: 'Ticket escalado (>48h)' },
  ]},
  calendar: { label: 'Calendario', icon: Calendar, color: '#8b5cf6', events: [
    { key: 'activity.reminder', label: 'Recordatorio de actividad' },
    { key: 'activity.overdue', label: 'Actividad vencida' },
  ]},
  financial: { label: 'Financiero', icon: CreditCard, color: '#f97316', events: [
    { key: 'invoice.overdue', label: 'Factura vencida' },
    { key: 'invoice.paid', label: 'Factura pagada' },
    { key: 'recurring.generated', label: 'Factura recurrente generada' },
  ]},
  system: { label: 'Sistema', icon: Shield, color: '#ef4444', events: [
    { key: 'system.error', label: 'Error Lambda crítico' },
    { key: 'system.alarm', label: 'Alarma CloudWatch' },
    { key: 'admin.login', label: 'Login admin' },
  ]},
}

function timeAgo(d) {
  if (!d) return ''
  const diff = Date.now() - new Date(d).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'Ahora'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

export default function NotificationsHub() {
  const [tab, setTab] = useState('feed')
  const [notifications, setNotifications] = useState([])
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showConfigForm, setShowConfigForm] = useState(false)
  const [configForm, setConfigForm] = useState({
    telegramBotToken: '', telegramChatId: '', telegramEnabled: false,
    webhookUrl: '', webhookEnabled: false,
    enabledEvents: [],
  })
  const [testResult, setTestResult] = useState(null)
  const [expandedNotif, setExpandedNotif] = useState(null)

  const loadNotifications = useCallback(async () => {
    try {
      const res = await adminApi.get('/admin/notifications')
      setNotifications(res.notifications || [])
    } catch (e) { console.error('loadNotifications:', e) }
  }, [])

  const loadConfig = useCallback(async () => {
    try {
      const res = await adminApi.get('/admin/notifications/config')
      const cfg = res.config || {}
      setConfig(cfg)
      setConfigForm({
        telegramBotToken: cfg.telegramBotToken || '',
        telegramChatId: cfg.telegramChatId || '',
        telegramEnabled: cfg.telegramEnabled || false,
        webhookUrl: cfg.webhookUrl || '',
        webhookEnabled: cfg.webhookEnabled || false,
        enabledEvents: cfg.enabledEvents ? JSON.parse(cfg.enabledEvents) : [],
      })
    } catch (e) { console.error('loadConfig:', e) }
  }, [])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadNotifications(), loadConfig()]).finally(() => setLoading(false))
  }, [loadNotifications, loadConfig])

  const handleSaveConfig = async () => {
    try {
      await adminApi.post('/admin/notifications/config', {
        ...configForm,
        enabledEvents: JSON.stringify(configForm.enabledEvents),
      })
      await loadConfig()
      setShowConfigForm(false)
    } catch (e) { console.error('saveConfig:', e) }
  }

  const handleTestTelegram = async () => {
    setTestResult(null)
    try {
      const res = await adminApi.post('/admin/notifications/test-telegram', {
        botToken: configForm.telegramBotToken,
        chatId: configForm.telegramChatId,
      })
      setTestResult(res.success ? '✅ Mensaje enviado correctamente' : `❌ Error: ${res.error}`)
    } catch (e) { setTestResult(`❌ Error: ${e.message}`) }
  }

  const handleMarkRead = async (id) => {
    try {
      await adminApi.patch(`/admin/notifications/${id}`, { read: true })
      setNotifications(ns => ns.map(n => n.$id === id ? { ...n, read: true } : n))
    } catch (e) { console.error('markRead:', e) }
  }

  const handleMarkAllRead = async () => {
    try {
      await adminApi.post('/admin/notifications/mark-all-read')
      setNotifications(ns => ns.map(n => ({ ...n, read: true })))
    } catch (e) { console.error('markAllRead:', e) }
  }

  const handleDeleteNotification = async (id) => {
    try {
      await adminApi.delete(`/admin/notifications/${id}`)
      setNotifications(ns => ns.filter(n => n.$id !== id))
    } catch (e) { console.error('deleteNotif:', e) }
  }

  const toggleEvent = (eventKey) => {
    setConfigForm(f => ({
      ...f,
      enabledEvents: f.enabledEvents.includes(eventKey)
        ? f.enabledEvents.filter(e => e !== eventKey)
        : [...f.enabledEvents, eventKey]
    }))
  }

  const toggleCategory = (categoryEvents) => {
    const keys = categoryEvents.map(e => e.key)
    const allEnabled = keys.every(k => configForm.enabledEvents.includes(k))
    setConfigForm(f => ({
      ...f,
      enabledEvents: allEnabled
        ? f.enabledEvents.filter(e => !keys.includes(e))
        : [...new Set([...f.enabledEvents, ...keys])]
    }))
  }

  const unreadCount = notifications.filter(n => !n.read).length
  const stats = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    return {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
      today: notifications.filter(n => (n.createdAt || '').startsWith(today)).length,
      critical: notifications.filter(n => n.priority === 'critical').length,
    }
  }, [notifications])

  const tabs = [
    { key: 'feed', icon: Bell, label: `Notificaciones${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
    { key: 'alerts', icon: AlertTriangle, label: 'Alertas' },
    { key: 'config', icon: Settings, label: 'Configuración' },
    { key: 'telegram', icon: MessageCircle, label: 'Telegram' },
  ]

  return (
    <div className="notif-page">
      <div className="notif-header">
        <div className="notif-title-row">
          <BellRing size={28} />
          <div>
            <h1>Centro de Notificaciones</h1>
            <p className="notif-subtitle">{stats.unread} sin leer · {stats.today} hoy</p>
          </div>
        </div>
        <div className="notif-header-actions">
          {tab === 'feed' && unreadCount > 0 && (
            <button className="notif-btn" onClick={handleMarkAllRead}><CheckCircle size={16} /> Marcar todo leído</button>
          )}
          <button className="notif-btn primary" onClick={() => { loadNotifications(); loadConfig() }} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
          </button>
        </div>
      </div>

      <div className="notif-tabs">
        {tabs.map(t => (
          <button key={t.key} className={`notif-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="notif-loading"><RefreshCw size={20} className="spin" /> Cargando...</div>
      ) : (
        <>
          {tab === 'alerts' && <AlertsSection />}

          {tab === 'feed' && (
            <div className="notif-feed">
              {notifications.length === 0 ? (
                <div className="notif-empty"><Bell size={40} /><p>No hay notificaciones</p></div>
              ) : notifications.map(n => {
                const catKey = (n.event || '').split('.')[0]
                const catInfo = EVENT_CATEGORIES[catKey] || EVENT_CATEGORIES.system
                const isExpanded = expandedNotif === n.$id
                return (
                  <div key={n.$id} className={`notif-item ${n.read ? 'read' : 'unread'} ${n.priority === 'critical' ? 'critical' : ''}`}>
                    <div className="notif-item-icon" style={{ background: `${catInfo.color}20`, color: catInfo.color }}>
                      <catInfo.icon size={18} />
                    </div>
                    <div className="notif-item-content" onClick={() => setExpandedNotif(isExpanded ? null : n.$id)}>
                      <div className="notif-item-main">
                        <span className="notif-item-title">{n.title}</span>
                        <span className="notif-item-time"><Clock size={12} /> {timeAgo(n.createdAt)}</span>
                      </div>
                      <p className="notif-item-body">{n.message}</p>
                      {isExpanded && n.details && (
                        <pre className="notif-item-details">{typeof n.details === 'string' ? n.details : JSON.stringify(n.details, null, 2)}</pre>
                      )}
                      <div className="notif-item-meta">
                        {n.channel && <span className="notif-tag">{CHANNEL_LABELS[n.channel] || n.channel}</span>}
                        {n.priority === 'critical' && <span className="notif-tag critical">Crítico</span>}
                      </div>
                    </div>
                    <div className="notif-item-actions">
                      {!n.read && <button className="notif-icon-btn" title="Marcar leído" onClick={() => handleMarkRead(n.$id)}><Eye size={14} /></button>}
                      <button className="notif-icon-btn danger" title="Eliminar" onClick={() => handleDeleteNotification(n.$id)}><Trash2 size={14} /></button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {tab === 'config' && (
            <div className="notif-config">
              <div className="notif-config-section">
                <h3>Eventos a Monitorear</h3>
                <p className="notif-config-desc">Seleccioná qué eventos generan notificaciones y se envían por Telegram</p>
                {Object.entries(EVENT_CATEGORIES).map(([catKey, cat]) => {
                  const allEnabled = cat.events.every(e => configForm.enabledEvents.includes(e.key))
                  const someEnabled = cat.events.some(e => configForm.enabledEvents.includes(e.key))
                  return (
                    <div key={catKey} className="notif-event-category">
                      <div className="notif-event-cat-header" onClick={() => toggleCategory(cat.events)}>
                        <cat.icon size={18} style={{ color: cat.color }} />
                        <span>{cat.label}</span>
                        <input type="checkbox" checked={allEnabled} readOnly className={someEnabled && !allEnabled ? 'indeterminate' : ''} />
                      </div>
                      <div className="notif-event-list">
                        {cat.events.map(ev => (
                          <label key={ev.key} className="notif-event-item">
                            <input type="checkbox" checked={configForm.enabledEvents.includes(ev.key)} onChange={() => toggleEvent(ev.key)} />
                            <span>{ev.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
                <button className="notif-btn primary" onClick={handleSaveConfig} style={{ marginTop: '1rem' }}>
                  <Save size={16} /> Guardar Configuración
                </button>
              </div>
            </div>
          )}

          {tab === 'telegram' && (
            <div className="notif-telegram">
              <div className="notif-telegram-card">
                <div className="notif-telegram-header">
                  <MessageCircle size={24} />
                  <h3>Integración Telegram</h3>
                  <label className="notif-toggle">
                    <input type="checkbox" checked={configForm.telegramEnabled} onChange={e => setConfigForm(f => ({ ...f, telegramEnabled: e.target.checked }))} />
                    <span className="notif-toggle-slider" />
                  </label>
                </div>

                <div className="notif-telegram-instructions">
                  <h4>Pasos para configurar:</h4>
                  <ol>
                    <li>Hablale a <strong>@BotFather</strong> en Telegram</li>
                    <li>Creá un bot nuevo con <code>/newbot</code></li>
                    <li>Copiá el <strong>Token</strong> que te da</li>
                    <li>Agregá el bot a un grupo o canal</li>
                    <li>Obtené el <strong>Chat ID</strong> con <code>/getUpdates</code> o <strong>@userinfobot</strong></li>
                  </ol>
                </div>

                <div className="notif-telegram-form">
                  <div className="notif-field">
                    <label>Bot Token</label>
                    <input type="password" value={configForm.telegramBotToken} onChange={e => setConfigForm(f => ({ ...f, telegramBotToken: e.target.value }))} placeholder="123456:ABC-DEF1234ghIkl-..." />
                  </div>
                  <div className="notif-field">
                    <label>Chat ID</label>
                    <input value={configForm.telegramChatId} onChange={e => setConfigForm(f => ({ ...f, telegramChatId: e.target.value }))} placeholder="-1001234567890" />
                  </div>
                </div>

                <div className="notif-telegram-actions">
                  <button className="notif-btn" onClick={handleTestTelegram} disabled={!configForm.telegramBotToken || !configForm.telegramChatId}>
                    <Send size={16} /> Enviar Mensaje de Prueba
                  </button>
                  <button className="notif-btn primary" onClick={handleSaveConfig}>
                    <Save size={16} /> Guardar
                  </button>
                </div>
                {testResult && <div className={`notif-test-result ${testResult.startsWith('✅') ? 'success' : 'error'}`}>{testResult}</div>}
              </div>

              <div className="notif-telegram-preview">
                <h4>Preview del mensaje</h4>
                <div className="notif-telegram-msg">
                  <strong>🌱 GrowPro Alert</strong><br /><br />
                  <strong>⚠️ Dispositivo Desconectado</strong><br />
                  MAC: AA:BB:CC:DD:EE:FF<br />
                  Cliente: Juan Pérez<br />
                  Último contacto: hace 5 min<br /><br />
                  <em>11/03/2026 14:30</em>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// ALERTS SECTION (aggregated from invoices, tickets, clients)
// ═══════════════════════════════════════════════════════════

function AlertsSection() {
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    async function loadAlerts() {
      try {
        const [invoicesRes, ticketsRes, clientsRes] = await Promise.all([
          adminApi.get('/admin/invoices').catch(() => ({ invoices: [] })),
          adminApi.get('/admin/tickets').catch(() => ({ tickets: [] })),
          adminApi.get('/admin/clients').catch(() => ({ clients: [] })),
        ])
        const now = new Date()
        const generated = []

        ;(invoicesRes.invoices || []).forEach(inv => {
          if (inv.status !== 'paid' && inv.status !== 'cancelled' && inv.dueDate && new Date(inv.dueDate) < now) {
            const days = Math.floor((now - new Date(inv.dueDate)) / 86400000)
            generated.push({
              id: `inv-${inv.$id}`, type: 'cobranza', severity: days > 30 ? 'critical' : 'warning',
              title: `Factura #${inv.invoiceNumber} vencida (${days}d)`,
              detail: `${inv.clientName} — $${(inv.total || 0).toLocaleString('es-AR')}`,
              date: inv.dueDate
            })
          }
        })

        ;(ticketsRes.tickets || []).forEach(t => {
          if (t.status === 'open' || t.status === 'in_progress') {
            generated.push({
              id: `tkt-${t.$id}`, type: 'soporte', severity: t.priority === 'high' ? 'critical' : 'info',
              title: `Ticket: ${t.subject}`,
              detail: `${t.clientName} — ${t.status === 'open' ? 'Abierto' : 'En progreso'}`,
              date: t.$createdAt
            })
          }
        })

        ;(clientsRes.clients || []).forEach(c => {
          if (c.isTrial) {
            const trialEnd = new Date(c.trialExpiresAt || c.subscriptionExpiresAt)
            const daysLeft = Math.ceil((trialEnd - now) / 86400000)
            if (daysLeft <= 3 && daysLeft >= 0) {
              generated.push({
                id: `trial-${c.$id}`, type: 'comercial', severity: 'warning',
                title: `Trial expira en ${daysLeft} día${daysLeft !== 1 ? 's' : ''}`,
                detail: c.name || c.email,
                date: c.trialExpiresAt || c.subscriptionExpiresAt
              })
            }
          }
        })

        generated.sort((a, b) => {
          const sev = { critical: 0, warning: 1, info: 2 }
          return (sev[a.severity] ?? 2) - (sev[b.severity] ?? 2)
        })

        setAlerts(generated)
      } catch (e) { console.error(e) }
      finally { setLoading(false) }
    }
    loadAlerts()
  }, [])

  const filteredAlerts = useMemo(() => {
    if (filter === 'all') return alerts
    return alerts.filter(a => a.type === filter)
  }, [alerts, filter])

  const severityIcon = (s) => s === 'critical' ? <AlertTriangle size={16} /> : s === 'warning' ? <Clock size={16} /> : <Bell size={16} />
  const severityColor = (s) => s === 'critical' ? '#f87171' : s === 'warning' ? '#f59e0b' : '#60a5fa'

  return (
    <>
      <div className="notif-alert-stats">
        <div className="notif-alert-stat"><span style={{ color: '#f87171' }}>{alerts.filter(a => a.severity === 'critical').length}</span><small>Críticas</small></div>
        <div className="notif-alert-stat"><span style={{ color: '#f59e0b' }}>{alerts.filter(a => a.severity === 'warning').length}</span><small>Advertencias</small></div>
        <div className="notif-alert-stat"><span style={{ color: '#60a5fa' }}>{alerts.filter(a => a.severity === 'info').length}</span><small>Info</small></div>
        <div className="notif-alert-stat"><span>{alerts.length}</span><small>Total</small></div>
      </div>

      <div className="notif-alert-filters">
        {[{ v: 'all', l: 'Todas' }, { v: 'cobranza', l: 'Cobranzas' }, { v: 'soporte', l: 'Soporte' }, { v: 'comercial', l: 'Comercial' }].map(f => (
          <button key={f.v} className={filter === f.v ? 'active' : ''} onClick={() => setFilter(f.v)}>{f.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="notif-loading"><RefreshCw size={20} className="spin" /> Cargando alertas...</div>
      ) : filteredAlerts.length === 0 ? (
        <div className="notif-empty"><CheckCircle size={48} style={{ color: '#4ade80' }} /><p>No hay alertas pendientes</p></div>
      ) : (
        <div className="notif-feed">
          {filteredAlerts.map(alert => (
            <div key={alert.id} className={`notif-item ${alert.severity}`}>
              <div className="notif-item-icon" style={{ color: severityColor(alert.severity) }}>{severityIcon(alert.severity)}</div>
              <div className="notif-item-content">
                <div className="notif-item-main">
                  <span className="notif-item-title">{alert.title}</span>
                </div>
                <p className="notif-item-body">{alert.detail}</p>
              </div>
              <div className="notif-item-actions">
                <span className="notif-tag">{alert.type}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
